// Access-policy controls for the email gate. Two portal settings drive how the
// contact form decides who gets in (server/services/email-blacklist.js):
//   - access_policy_mode: 'allowlist' (only explicitly-allowed domains get in)
//     vs 'blocklist' (everyone except blocked/disposable domains gets in).
//   - email_verification_required: whether a magic-link verification is sent
//     before a request can proceed.
// Both are admin-only portal settings; this section reads + writes them via the
// admin settings API and saves each change immediately.
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Paper, Typography, Box, ToggleButton, ToggleButtonGroup, Switch,
  FormControlLabel, Alert, CircularProgress, Stack,
} from '@mui/material';
import { fetchAdminSettings, saveAdminSettings } from '../../../store/admin-settings-slice.js';

const MODE_HELP = {
  allowlist: 'Strict: only addresses on the allowlist (and built-in defaults like *.gov / *.mil / *.us) can reach the form. Everyone else is turned away.',
  blocklist: 'Permissive: everyone can reach the form except blocked domains, the disposable-mail list, and the default blocked competitors.',
};

const AccessPolicySection = () => {
  const dispatch = useDispatch();
  const {
    settings, loading, error, saving, saveError,
  } = useSelector((s) => s.adminSettings);

  useEffect(() => {
    if (!settings) dispatch(fetchAdminSettings());
  }, [dispatch, settings]);

  const mode = settings?.access_policy_mode ?? 'allowlist';
  const verificationRequired = settings?.email_verification_required ?? true;

  const handleMode = (_e, next) => {
    if (!next || next === mode) return;
    dispatch(saveAdminSettings({ access_policy_mode: next }));
  };

  const handleVerification = (e) => {
    dispatch(saveAdminSettings({ email_verification_required: e.target.checked }));
  };

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2,
      }}
      >
        <Typography variant="h6" component="h2" sx={{ fontWeight: 700 }}>
          Access policy
        </Typography>
        {saving && <CircularProgress size={18} aria-label="Saving" />}
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Choose how the contact form decides who can request pricing, and whether
        visitors must verify their email first.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}

      {loading && !settings ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={22} aria-label="Loading access policy" />
        </Box>
      ) : (
        <Stack spacing={2.5}>
          <Box>
            <ToggleButtonGroup
              value={mode}
              exclusive
              onChange={handleMode}
              size="small"
              aria-label="Access policy mode"
            >
              <ToggleButton value="allowlist">Allowlist (strict)</ToggleButton>
              <ToggleButton value="blocklist">Blocklist (permissive)</ToggleButton>
            </ToggleButtonGroup>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {MODE_HELP[mode]}
            </Typography>
          </Box>

          <FormControlLabel
            control={(
              <Switch
                checked={verificationRequired}
                onChange={handleVerification}
              />
            )}
            label="Require email verification (magic link)"
          />
        </Stack>
      )}
    </Paper>
  );
};

export default AccessPolicySection;
